import { axios } from './axios';
import {
  CreateCitationDto,
  UpdateCitationDto
} from '@/jbi-shared/types/citation.dto';

/**
 * Interface definitions for pending citation operations
 */
export interface PendingCitationResponse {
  id: number;
  citationId: number;
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED';
  createdAt: string;
  citation: {
    id: number;
    content: any;
    projectId: number;
    createdAt: string;
  };
}

export interface PendingCitationCounts {
  active: number;
  archived: number;
  newlyAdded: number;
  total: number;
  referenced: number;
}

export interface PendingCitationListResponse {
  citations: PendingCitationResponse[];
  counts: PendingCitationCounts;
}

export interface BulkImportCitationsResponse {
  importedCitations: PendingCitationResponse[];
  successCount: number;
  failureCount: number;
  errors?: string[];
}

/**
 * API interface for pending citation management
 */
export const pendingCitationsApi = {
  /**
   * Get all citations for a pending document
   * @param pendingDocumentId ID of the pending document
   * @returns List of citations with counts
   */
  async getPendingDocumentCitations(
    pendingDocumentId: number
  ): Promise<PendingCitationListResponse> {
    const response = await axios.get(
      `pending-documents/${pendingDocumentId}/citations`
    );
    return response.data;
  },

  /**
   * Add a new citation to a pending document
   * @param pendingDocumentId ID of the pending document
   * @param citationData Citation data to create
   * @returns The created citation mapping
   */
  async addCitationToPendingDocument(
    pendingDocumentId: number,
    citationData: CreateCitationDto
  ): Promise<PendingCitationResponse> {
    const response = await axios.post(
      `pending-documents/${pendingDocumentId}/citations`,
      citationData
    );
    return response.data;
  },

  /**
   * Import multiple citations to a pending document
   * @param pendingDocumentId ID of the pending document
   * @param citationsData Array of citation data to import
   * @returns Array of imported citation mappings
   */
  async importCitationsToPendingDocument(
    pendingDocumentId: number,
    citationsData: CreateCitationDto[]
  ): Promise<PendingCitationResponse[]> {
    const response = await axios.post(
      `pending-documents/${pendingDocumentId}/citations/import`,
      citationsData
    );
    return response.data;
  },

  /**
   * Get active citations for a pending document
   * @param pendingDocumentId ID of the pending document
   * @returns Array of active citation mappings
   */
  async getActiveCitations(
    pendingDocumentId: number
  ): Promise<PendingCitationResponse[]> {
    const response = await axios.get(
      `pending-documents/${pendingDocumentId}/citations/active`
    );
    return response.data;
  },

  /**
   * Get archived citations for a pending document
   * @param pendingDocumentId ID of the pending document
   * @returns Array of archived citation mappings
   */
  async getArchivedCitations(
    pendingDocumentId: number
  ): Promise<PendingCitationResponse[]> {
    const response = await axios.get(
      `pending-documents/${pendingDocumentId}/citations/archived`
    );
    return response.data;
  },

  /**
   * Update citation status in a pending document
   * @param pendingDocumentId ID of the pending document
   * @param citationId ID of the citation
   * @param status New status for the citation
   * @returns Updated citation mapping
   */
  async updateCitationStatus(
    pendingDocumentId: number,
    citationId: number,
    status: 'ACTIVE' | 'ARCHIVED' | 'DELETED'
  ): Promise<PendingCitationResponse> {
    const response = await axios.put(
      `pending-documents/${pendingDocumentId}/citations/${citationId}/status`,
      { status }
    );
    return response.data;
  },

  /**
   * Archive a citation in a pending document
   * @param pendingDocumentId ID of the pending document
   * @param citationId ID of the citation to archive
   */
  async archiveCitation(
    pendingDocumentId: number,
    citationId: number
  ): Promise<void> {
    await axios.put(
      `pending-documents/${pendingDocumentId}/citations/${citationId}/archive`
    );
  },

  /**
   * Restore (activate) a citation in a pending document
   * @param pendingDocumentId ID of the pending document
   * @param citationId ID of the citation to restore
   */
  async restoreCitation(
    pendingDocumentId: number,
    citationId: number
  ): Promise<void> {
    await axios.put(
      `pending-documents/${pendingDocumentId}/citations/${citationId}/restore`
    );
  },

  /**
   * Soft delete a citation from a pending document
   * @param pendingDocumentId ID of the pending document
   * @param citationId ID of the citation to delete
   */
  async deleteCitation(
    pendingDocumentId: number,
    citationId: number
  ): Promise<void> {
    await axios.delete(
      `pending-documents/${pendingDocumentId}/citations/${citationId}`
    );
  },

  /**
   * Update a citation's content in a pending document
   * @param pendingDocumentId ID of the pending document
   * @param citationId ID of the citation to update
   * @param citationData New citation data
   */
  async updateCitation(
    pendingDocumentId: number,
    citationId: number,
    citationData: UpdateCitationDto
  ): Promise<PendingCitationResponse> {
    const response = await axios.put(
      `pending-documents/${pendingDocumentId}/citations/${citationId}`,
      citationData
    );
    return response.data;
  }
};
